import { ReactElement } from "react";
import { Box, Grid, useBreakpointValue } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { phraseVisualizerType, phraseVisualizerTypeAtom } from "../../store";
import { ReactSVG } from "react-svg";
import barImage from "../../assets/images/base/recording_bar.svg";
import circleImage from "../../assets/images/base/recording_circle.svg";
import waveImage from "../../assets/images/base/recording_wave.svg";
import { setSVGFillColor, setSVGStrokeColor } from "../../utils/svgHelper";
import { useColorToken } from "../../hooks/useColorToken";

interface VisualizerSelectButtonProps {
  visualizerType: phraseVisualizerType;
}

function VisualizerSelectButton({
  visualizerType,
}: Readonly<VisualizerSelectButtonProps>): ReactElement {
  const commonBase = useColorToken("common.base");
  const textMainLv1 = useColorToken("text.main_text_lv1");
  const themeLv1 = useColorToken("primary.theme_lv1");
  const themeLv2 = useColorToken("primary.theme_lv2");
  const themeLv3 = useColorToken("primary.theme_lv3");
  const accentColor = useColorToken("primary.accent");
  const bgLv1 = useColorToken("primary.bg_lv1");
  const [currentPhraseVisualizerType, setPhraseVisualizerType] = useAtom(
    phraseVisualizerTypeAtom,
  );
  const handleClick = (type: phraseVisualizerType): void => {
    setPhraseVisualizerType(type);
  };

  let srcImage = "";
  switch (visualizerType) {
    case "Bar":
      srcImage = barImage;
      break;
    case "Wave":
      srcImage = waveImage;
      break;
    default:
      srcImage = circleImage;
  }

  return (
    <Box
      as="button"
      padding={4}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      onClick={() => handleClick(visualizerType)}
    >
      <ReactSVG
        src={srcImage}
        beforeInjection={(svg) => {
          svg.setAttribute("width", "100%");
          setSVGFillColor(svg, ".theme_lv1", themeLv1);
          setSVGFillColor(svg, ".theme_lv2", themeLv2);
          setSVGFillColor(svg, ".theme_lv3", themeLv3);
          setSVGFillColor(svg, ".bg_lv1", bgLv1);
          setSVGFillColor(svg, ".accent", accentColor);
          setSVGFillColor(svg, ".main_text_lv1", textMainLv1);
          setSVGStrokeColor(svg, ".cls-1, .cls-3, .cls-4", themeLv1);
          setSVGStrokeColor(svg, ".cls-2", themeLv2);
          if (visualizerType === "Circle") {
            setSVGFillColor(svg, ".cls-3,.cls-4", commonBase);
          }
          if (visualizerType === "Bar") {
            setSVGFillColor(svg, ".cls-5,.cls-3,.cls-4", commonBase);
            setSVGStrokeColor(svg, ".cls-3, .cls-4, .cls-5, .cls-6", themeLv1);
          }
          if (visualizerType === "Wave") {
            setSVGStrokeColor(svg, ".cls-1", accentColor);
            setSVGStrokeColor(svg, ".cls-5", themeLv3);
            setSVGStrokeColor(svg, ".cls-2, cls-3", themeLv2);
            setSVGStrokeColor(svg, ".cls-4, .cls-5, .cls-6, .cls-7", themeLv1);
            setSVGFillColor(svg, ".cls-10, .cls-5,.cls-7,.cls-8", commonBase);
          }
          if (currentPhraseVisualizerType === visualizerType) {
            setSVGFillColor(svg, ".select", "red");
          }
        }}
      />
    </Box>
  );
}

export function VisualizerSelect(): ReactElement {
  const templateColumns = useBreakpointValue({
    base: "1fr",
    md: "repeat(2, 1fr)",
  });

  return (
    <Grid templateColumns={templateColumns} gap={2}>
      <VisualizerSelectButton visualizerType="Circle" />
      <VisualizerSelectButton visualizerType="Bar" />
      <VisualizerSelectButton visualizerType="Wave" />
    </Grid>
  );
}
